import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Button,
  Card,
} from '@design-system';
import { useSelector } from 'react-redux';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { selectClient } from '@/model';
import { CARRIERS_WITHOUT_QUOTE_CREATION } from '@shared/constants';
import { formatCurrency } from '@/lib';
import { LtcCostDisplay } from '@client/components/LtcCostDisplay';
import { getPolicyTypeForQuote } from '@shared/calculations/roi/calcClientROIProjections';
import { ProductDetails } from '@/components/ProductDetails';
import { GlobalBenefitsBadge } from '@/components/GlobalBenefitsBadge';
import { JointPolicyDeleteDialog } from '@/components/JointPolicyDeleteDialog';
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog';

import {
  BenefitPeriodBadge,
  CreateQuoteButton,
  CouplesDiscountBadge,
  MissingAdvisorAddressDialog,
  QuoteFeaturesAndBenefits,
  QuoteProductInfo,
  QuoteROIDisplay,
  useIllustrationManagement,
  useIllustrationPolling,
  usePolicyManagement,
  useQuoteCardDialogs,
  calcProjectedMonthlyBenefit,
  type BenefitDesignQuoteGroup,
  type BenefitDesignPayStructure,
  type QuoteWithROI,
} from '@/features/shop-insurance';
import { getAssetCareBenefitPeriodLabel } from '@/features/shop-insurance/lib/benefit-period-label';
import { UnderwritingStatus } from '@client/features/underwriting-eligibility';

interface BenefitDesignQuoteCardProps {
  group: BenefitDesignQuoteGroup;
  onClose: () => void;
  selectedTab?: ShopInsuranceTab;
  clientPartner?: Client | null;
  isQuoteSelected: (quote: any) => { isSelected: boolean; fundingSourceId: string | null };
  testId?: string;
  careStartAge?: number;
  yearsUntilLtc: number;
}

function BenefitDesignPayStructureRow({
  payStructure,
  selectedTab,
  clientPartner,
  onClose,
  isQuoteSelected,
}: {
  payStructure: BenefitDesignPayStructure;
  selectedTab: ShopInsuranceTab;
  clientPartner: Client | null;
  onClose: () => void;
  isQuoteSelected: (quote: any) => { isSelected: boolean; fundingSourceId: string | null };
}) {
  const { flags } = useFeatureFlags();
  const { isSelected, fundingSourceId } = isQuoteSelected(payStructure.originalQuote);
  const formattedROI = (payStructure.originalQuote as QuoteWithROI).formattedROI;

  useIllustrationPolling();

  const {
    handleAddPolicy,
    handleRemovePolicy,
    deletePolicyForClientOnly,
    deletePolicyForBothClients,
  } = usePolicyManagement({
    quote: payStructure.originalQuote,
    selectedTab,
    clientPartner,
    fundingSourceId,
    onClose,
  });

  const {
    hasIllustrationPdf,
    isCreatingIllustration,
    handleViewPdf,
    handleCreateIllustration,
    isMissingAdvisorAddressDialogOpen,
    closeMissingAdvisorAddressDialog,
    handleMissingAdvisorAddressResolved,
  } = useIllustrationManagement({
    quote: payStructure.originalQuote,
    selectedPremium: payStructure.premiumAmount,
    clientPartner,
  });

  const {
    showJointPolicyDeleteDialog,
    jointPolicyDeleteMode,
    showQuoteGenerationInfoDialog,
    setJointPolicyDeleteMode,
    handleJointPolicyDeleteCancel,
    handleJointPolicyDeleteConfirm,
    showJointDialog,
    showQuoteGenerationInfoDialogFn,
    hideQuoteGenerationInfoDialog,
  } = useQuoteCardDialogs();

  const isQuoteCreationUnavailable = CARRIERS_WITHOUT_QUOTE_CREATION.includes(
    payStructure.originalQuote.carrierName as any,
  );

  const client = useSelector(selectClient);

  return (
    <>
      <AccordionItem
        value={payStructure.insuranceQuoteId}
        className="not-first:border-t mb-0 rounded-none border-0 border-dashed"
      >
        <AccordionTrigger className="items-center gap-3 rounded-none px-4 py-2.5 text-sm transition-colors sm:gap-4 [&>svg]:size-4 [&>svg]:translate-y-0">
          <div className="flex flex-1 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-4">
              <span className="text-muted-foreground w-18 shrink-0 text-sm sm:w-24">
                {payStructure.payLabel}
              </span>
              <span className="text-primary-dark text-sm font-semibold">
                {formatCurrency(payStructure.premiumAmount)}
                {payStructure.isAnnual && (
                  <span className="text-muted-foreground ml-0.5 text-xs font-normal">/yr</span>
                )}
              </span>
              {formattedROI && <Badge variant="success">ROI {formattedROI}</Badge>}
            </div>

            <div
              className="flex w-full shrink-0 gap-2 sm:w-auto"
              onClick={event => event.stopPropagation()}
            >
              {isSelected ? (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="bg-destructive/10 text-destructive hover:bg-destructive/15 h-8 grow rounded-sm text-xs sm:grow-0"
                  onClick={clickEvent => handleRemovePolicy(clickEvent, showJointDialog)}
                >
                  Remove
                </Button>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  className="h-8 grow rounded-sm text-xs sm:grow-0"
                  onClick={handleAddPolicy}
                >
                  Select
                </Button>
              )}

              {!isQuoteCreationUnavailable && !flags.nycLifeDemo && (
                <CreateQuoteButton
                  testId={`benefit-design-create-quote-${payStructure.insuranceQuoteId}`}
                  onClick={
                    hasIllustrationPdf
                      ? handleViewPdf
                      : event => handleCreateIllustration(event, showQuoteGenerationInfoDialogFn)
                  }
                  disabled={isCreatingIllustration}
                  isLoading={isCreatingIllustration}
                  isCompleted={Boolean(hasIllustrationPdf)}
                  defaultText="Quote"
                  loadingText="Generating..."
                  completedText="View"
                  showCompletedIcon={false}
                  className="h-8 grow rounded-sm text-xs sm:grow-0"
                />
              )}
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="border-t border-dashed [&>div]:mt-4">
          <ProductDetails quote={payStructure.originalQuote} />
        </AccordionContent>
      </AccordionItem>

      <JointPolicyDeleteDialog
        open={showJointPolicyDeleteDialog}
        onClose={handleJointPolicyDeleteCancel}
        onConfirm={mode =>
          handleJointPolicyDeleteConfirm(
            mode,
            deletePolicyForBothClients,
            deletePolicyForClientOnly,
          )
        }
        deleteMode={jointPolicyDeleteMode}
        onDeleteModeChange={setJointPolicyDeleteMode}
        clientName={client?.clientFirstName}
        partnerName={clientPartner?.clientFirstName}
      />

      <UnsavedChangesDialog
        showWarning={showQuoteGenerationInfoDialog}
        handleCancelClose={hideQuoteGenerationInfoDialog}
        handleConfirmClose={hideQuoteGenerationInfoDialog}
        dialogTitle="Please wait for current quote generation"
        dialogDescription="We're currently generating a quote for another product. Since this process takes 1-2 minutes, please wait for it to complete before creating a new quote. You can still view any previously generated quotes."
        primaryButtonText="Got it"
        secondaryButtonText=""
        destructive={false}
        showIcon={true}
      />

      <MissingAdvisorAddressDialog
        open={isMissingAdvisorAddressDialogOpen}
        onClose={closeMissingAdvisorAddressDialog}
        onResolved={handleMissingAdvisorAddressResolved}
      />
    </>
  );
}

export function BenefitDesignQuoteCard({
  group,
  onClose,
  selectedTab = 'single',
  clientPartner = null,
  isQuoteSelected,
  testId,
  careStartAge,
  yearsUntilLtc,
}: BenefitDesignQuoteCardProps) {
  const representativeQuote = group.representativeQuote as QuoteWithROI;
  const projectedBenefit = calcProjectedMonthlyBenefit(representativeQuote, yearsUntilLtc);

  // The pill reflects the card's actual contents, not just the representative
  // quote: policyType isn't part of the group key, so a single card can mix
  // Couples Discount and non-couples pay structures. Show the pill when any
  // pay structure is a Couples Discount quote.
  const hasCouplesDiscount = group.payStructures.some(
    payStructure => payStructure.originalQuote.policyType === 'Couples Discount',
  );
  // AOB and COB periods are part of the group key, so the representative quote's
  // periods are every pay structure's.
  const benefitPeriodLabel = getAssetCareBenefitPeriodLabel(representativeQuote);
  const hasFeaturesAndBenefits = Boolean(
    representativeQuote.hasGlobalBenefits || hasCouplesDiscount || benefitPeriodLabel,
  );

  return (
    <Card
      data-testid={testId}
      data-quote-id={representativeQuote.insuranceQuoteId}
      className="hover:border-primary hover:ring-primary/10 w-full cursor-pointer gap-0 p-0 ring-4 ring-transparent transition-all duration-200"
    >
      <div className="relative">
        <div className="p-4">
          <div className="flex flex-wrap items-start gap-x-8 gap-y-3">
            <div className="flex flex-col gap-2">
              <QuoteProductInfo
                carrierName={group.carrierName}
                productName={group.productName}
                showLogo={true}
                showRating={true}
              />
            </div>
            <div className="flex flex-wrap items-start gap-8">
              <QuoteROIDisplay
                projectedCostCoverage={representativeQuote.projectedCostCoverage}
                partnerProjectedCostCoverage={representativeQuote.partnerProjectedCostCoverage}
                policyType={getPolicyTypeForQuote(group.representativeQuote)}
              />
              {representativeQuote.ltcBenefitMonthlyIndividual != null && (
                <div className="flex flex-col gap-1">
                  <span className="text-muted-foreground text-xs">
                    Monthly Benefit
                    {representativeQuote.primaryAge != null
                      ? ` (age ${representativeQuote.primaryAge})`
                      : ''}
                  </span>
                  <div className="flex items-baseline gap-1">
                    <span className="text-primary-dark text-lg font-bold">
                      <LtcCostDisplay value={representativeQuote.ltcBenefitMonthlyIndividual} />
                    </span>
                    <span className="text-muted-foreground text-xs">/month</span>
                  </div>
                </div>
              )}
              {projectedBenefit != null && (
                <div className="flex flex-col gap-1">
                  <span className="text-muted-foreground text-xs">
                    {careStartAge ? `At LTC Start (age ${careStartAge})` : 'At LTC Start'}
                  </span>
                  <div className="flex items-baseline gap-1">
                    <span className="text-primary-dark text-lg font-bold">
                      <LtcCostDisplay value={projectedBenefit} />
                    </span>
                    <span className="text-muted-foreground text-xs">/month</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div
            className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2"
            onClick={event => event.stopPropagation()}
          >
            {hasFeaturesAndBenefits && (
              <QuoteFeaturesAndBenefits>
                {benefitPeriodLabel && <BenefitPeriodBadge label={benefitPeriodLabel} />}
                <GlobalBenefitsBadge
                  hasGlobalBenefits={representativeQuote.hasGlobalBenefits}
                  appearance="inline"
                />
                {hasCouplesDiscount && <CouplesDiscountBadge appearance="inline" />}
              </QuoteFeaturesAndBenefits>
            )}
            {/* This card has no action column, so the check goes to the far end of the row. Beside
                the feature ticks it read as a check of "International Benefits". */}
            <div
              data-testid="quote-card-eligibility"
              className="ml-auto"
            >
              <UnderwritingStatus
                productName={representativeQuote.productName}
                fundingPolicyType={representativeQuote.fundingPolicyType}
                policyType={representativeQuote.policyType}
                appearance="quote-card-link"
              />
            </div>
          </div>

          <Accordion
            type="multiple"
            className="border-border mt-4 overflow-hidden rounded-md border"
          >
            {group.payStructures.map(payStructure => (
              <BenefitDesignPayStructureRow
                key={payStructure.insuranceQuoteId}
                payStructure={payStructure}
                selectedTab={selectedTab}
                clientPartner={clientPartner}
                onClose={onClose}
                isQuoteSelected={isQuoteSelected}
              />
            ))}
          </Accordion>
        </div>
      </div>
    </Card>
  );
}
